import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './feedback.scss';

const ICONS = {
  danger: 'bi-exclamation-triangle-fill',
  warning: 'bi-exclamation-circle-fill',
  primary: 'bi-question-circle-fill',
};

const ConfirmationDialog = ({
  open,
  title,
  message,
  confirmLabel,
  cancelLabel,
  busyLabel,
  variant,
  busy,
  onConfirm,
  onCancel,
}) => {
  const confirmButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    confirmButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [busy, onCancel, open]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="feedback-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <section
        className="feedback-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="feedback-dialog-title"
        aria-describedby="feedback-dialog-message"
      >
        <div className={`feedback-dialog-icon text-${variant}`} aria-hidden="true">
          <i className={`bi ${ICONS[variant] || ICONS.primary}`}></i>
        </div>
        <div className="feedback-dialog-copy">
          <h2 className="h5 mb-2" id="feedback-dialog-title">{title}</h2>
          <div className="text-muted" id="feedback-dialog-message">{message}</div>
        </div>
        <div className="feedback-dialog-actions">
          <button className="btn btn-sm btn-outline-secondary" type="button" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </button>
          <button
            className={`btn btn-sm btn-${variant}`}
            type="button"
            onClick={onConfirm}
            disabled={busy}
            ref={confirmButtonRef}
          >
            {busy ? (
              <><span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>{busyLabel}</>
            ) : confirmLabel}
          </button>
        </div>
      </section>
    </div>,
    document.body
  );
};

ConfirmationDialog.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.node,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  busyLabel: PropTypes.string,
  variant: PropTypes.oneOf(['primary', 'warning', 'danger']),
  busy: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ConfirmationDialog.defaultProps = {
  open: false,
  title: 'Confirm action',
  message: '',
  confirmLabel: 'Continue',
  cancelLabel: 'Cancel',
  busyLabel: 'Working...',
  variant: 'primary',
  busy: false,
};

export default ConfirmationDialog;

